import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./MentorFooter.module.css";
import {
  completeMentorSessionEventBuilder,
  handleButtonClickEventBuilder,
  sendEvent,
} from "../../utils/event-helpers";
import { addUrlParams } from "../../api";
import {
  NAME_URL_PARAM,
  SESSION_ID_URL_PARAM,
  TIMESTAMP_START_URL_PARAM,
} from "../../pages/Transition/Transition";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { usePromptTimer } from "./usePromptTimer";
import type { SelectedMentorContent } from "../MentorBrowser/helpers/types";
import MentorTimeoutDialog from "../Dialogs/MentorTimeoutDialog";
import useTenant from "../../tenancy/useTenantPath";

interface FooterWithTimerProps {
  enableTimer: boolean;
  selectedMentorContent?: SelectedMentorContent;
}

const MentorFooter = ({
  enableTimer,
  selectedMentorContent,
}: FooterWithTimerProps) => {
  const snoozeTimeInSeconds = 5 * 60; // 5 minutes for snooze

  const [openModal, setOpenModal] = useState(false);
  const [isSnoozed, setIsSnoozed] = useState(false);

  const { userId, sessionId, postSurveyTimeInSeconds } = useAppSelector(
    (state) => state.session,
  );
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const tenant = useTenant();

  const reminderTimer = usePromptTimer({
    durationSeconds: isSnoozed ? snoozeTimeInSeconds : postSurveyTimeInSeconds,
    onComplete: () => {
      localStorage.setItem("hasPassedMentorTimer", "true");
      setOpenModal(true);
    },
    startImmediately: isSnoozed ? true : enableTimer,
  });

  const postSurveyTimeInMinutes = Math.floor(postSurveyTimeInSeconds / 60);

  const formatProgress = (elapsed: number, target: number): string => {
    const count = Math.floor(elapsed / 60);
    const total = Math.floor(target / 60);
    return `${count.toString()}/${total.toString()}`;
  };

  const handleExtendTime = () => {
    setOpenModal(false);
    setIsSnoozed(true);
    reminderTimer.reset();

    if (!userId || !sessionId) return;
    const eventData = handleButtonClickEventBuilder(
      userId,
      sessionId,
      "Snooze",
      {},
    );
    void dispatch(sendEvent(eventData));
  };

  const navigateToRecommenderWithParams = () => {
    const ONE_DAY_AGO = new Date(
      Date.now() - 24 * 60 * 60 * 1000,
    ).toISOString();
    const navigateTarget = addUrlParams(tenant.basePath + "/transition", {
      [TIMESTAMP_START_URL_PARAM]: ONE_DAY_AGO,
      ...(sessionId && { [SESSION_ID_URL_PARAM]: sessionId }),
      ...(userId && { [NAME_URL_PARAM]: userId }),
    });
    navigate(navigateTarget);
  };

  const handleGoToRecommendations = () => {
    setOpenModal(false);
    navigateToRecommenderWithParams();

    if (!userId || !sessionId) return;
    const eventData = handleButtonClickEventBuilder(
      userId,
      sessionId,
      "Go To Recommendations",
      {},
    );
    void dispatch(sendEvent(eventData));
  };

  const handlePrevious = () => {
    if (!userId || !sessionId || !selectedMentorContent) return;

    const eventData = completeMentorSessionEventBuilder(
      userId,
      sessionId,
      selectedMentorContent.mentors,
      {},
    );
    void dispatch(sendEvent(eventData));

    navigate(tenant.basePath + "/mentorpal");
  };

  const handleNext = () => {
    if (!userId || !sessionId) return;
    const eventData = handleButtonClickEventBuilder(
      userId,
      sessionId,
      "Go To Recommendations",
      {},
    );
    void dispatch(sendEvent(eventData));
    navigateToRecommenderWithParams();
  };

  return (
    <div className={styles.footerWithTimerContainer}>
      <div className={styles.footerWithTimerContent}>
        <button
          className={styles.backButton}
          onClick={handlePrevious}
          disabled={!selectedMentorContent}
          data-cy="timer-footer-back-button"
        >
          ← Back
        </button>

        {enableTimer ? (
          <div className={styles.footerTimer}>
            {isSnoozed
              ? `Snoozed For: ${formatProgress(
                  reminderTimer.elapsedSeconds,
                  snoozeTimeInSeconds,
                )} min`
              : `Time Elapsed: ${formatProgress(
                  reminderTimer.elapsedSeconds,
                  postSurveyTimeInSeconds,
                )} min`}
          </div>
        ) : (
          <div>{/* placeholder */}</div>
        )}

        <button
          className={styles.nextButton}
          onClick={() => {
            handleNext();
          }}
          disabled={
            !isSnoozed &&
            enableTimer &&
            localStorage.getItem("hasPassedMentorTimer") !== "true"
          }
          data-cy="timer-footer-next-button"
        >
          Get Recommendations →
        </button>

        {/* Modal */}
        <MentorTimeoutDialog
          open={openModal}
          postSurveyTimeInMinutes={postSurveyTimeInMinutes}
          onSnooze={handleExtendTime}
          onGoToRecommendations={handleGoToRecommendations}
        />
      </div>
    </div>
  );
};

export default MentorFooter;
